// One place that decides what stage a job is in, so every screen agrees.
//
// A "job" is a quote plus its soft-hold booking. Payment is acceptance, so
// payment_status beats quote status as the confirmation signal.

export const STAGE = {
  DRAFT: "draft",
  QUOTED: "quoted",
  DEPOSIT: "deposit",
  PAID: "paid",
  COMPLETED: "completed",
  LOST: "lost",
};

export const STAGE_META = {
  [STAGE.DRAFT]: { label: "Draft", cls: "bg-birdie-warmgray/20 text-birdie-charcoal/70" },
  [STAGE.QUOTED]: { label: "Awaiting payment", cls: "bg-birdie-mustard/25 text-birdie-mustard-dark" },
  [STAGE.DEPOSIT]: { label: "Deposit paid", cls: "bg-blue-100 text-blue-700" },
  [STAGE.PAID]: { label: "Paid in full", cls: "bg-birdie-green/15 text-birdie-green" },
  [STAGE.COMPLETED]: { label: "Completed", cls: "bg-birdie-warmgray/20 text-birdie-charcoal/60" },
  [STAGE.LOST]: { label: "Lost", cls: "bg-red-100 text-red-700" },
};

export function isPaid(job) {
  return job.payment_status === "paid" || Boolean(job.paid_at);
}

/** What the customer has actually handed over so far. */
export function amountPaidCents(job) {
  return Number(job.paid_amount_cents || 0);
}

/** What's still owed. Zero once the job is settled, or rained out (the
 * unpaid part is written off and the rest became a credit). */
export function balanceCents(job) {
  if (job.rained_out_at) return 0;
  return Math.max(0, Number(job.total_cents || 0) - amountPaidCents(job));
}

/** End of the event (falls back to the start) in ms, or 0 if undated. */
export function jobEndMs(job) {
  const raw = job.event_end || job.event_date;
  if (!raw) return 0;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function stageOf(job, nowMs = Date.now()) {
  if (job.status === "declined") return STAGE.LOST;
  if (job.status === "draft") return STAGE.DRAFT;

  const end = jobEndMs(job);
  const isPast = Boolean(end) && end < nowMs;

  if (isPaid(job)) {
    if (isPast) return STAGE.COMPLETED;
    // A deposit confirms the job but still owes — worth seeing at a glance.
    return balanceCents(job) > 0 ? STAGE.DEPOSIT : STAGE.PAID;
  }
  if (job.status === "signed") return isPast ? STAGE.COMPLETED : STAGE.PAID;
  return STAGE.QUOTED;
}

/** Money still owed on this job (deposit if one was set, else the total). */
export function amountDueCents(job) {
  const deposit = Number(job.deposit_cents || 0);
  return deposit > 0 ? deposit : Number(job.total_cents || 0);
}

/** Sent, not paid — the jobs that are actually waiting on the customer. */
export function isAwaitingPayment(job, nowMs = Date.now()) {
  return stageOf(job, nowMs) === STAGE.QUOTED && job.status === "sent";
}

/** Quoted but never sent — sitting in your drafts. */
export function isUnsentDraft(job, nowMs = Date.now()) {
  return stageOf(job, nowMs) === STAGE.DRAFT;
}

/** Happening between now and `days` from now. */
export function isUpcoming(job, days = 7, nowMs = Date.now()) {
  const start = job.event_date ? new Date(job.event_date).getTime() : 0;
  if (!start || Number.isNaN(start)) return false;
  return start >= nowMs && start <= nowMs + days * 86400000;
}

/** Sent, unpaid, and past its valid_until date. */
export function isExpired(job, nowMs = Date.now()) {
  if (!isAwaitingPayment(job, nowMs) || !job.valid_until) return false;
  // valid_until is a date; treat it as expiring at end of that day.
  const until = new Date(`${String(job.valid_until).slice(0, 10)}T23:59:59`).getTime();
  return !Number.isNaN(until) && until < nowMs;
}

/** Public "view & pay" page for a job, or "" if it has no token yet. */
export function payPageUrl(job) {
  return job.public_token ? `${window.location.origin}/pay/${job.public_token}` : "";
}

export function sortByEventDate(a, b) {
  const ax = new Date(a.event_date || a.created_at || 0).getTime();
  const bx = new Date(b.event_date || b.created_at || 0).getTime();
  return ax - bx;
}
